/**
 * CUSUM level-shift detection.
 *
 * Reported independently of the piecewise-linear trend phases: this finds
 * discrete points where the *mean level* changes, using cumulative-sum
 * change-point analysis (locate the point of maximum cumulative deviation,
 * test the standardized mean difference across it, then recurse on each side).
 *
 * Every shift carries a `sharpness` (the jump at the change-point relative to
 * the typical sample-to-sample change) so the agent can distinguish an abrupt
 * step from a gradual drift — the same drift the segmentation layer would
 * report as one trending phase. Two complementary fact-streams; no merging.
 */
import { mean, median, standardDeviation } from 'simple-statistics';

import { EPSILON } from './constants.ts';
import type { LevelShift, Sensitivity, TimeseriesPoint } from './types.ts';

/**
 * Minimum standardized mean difference (vs the whole-series spread) for a
 * level shift to register. Lower → detects smaller shifts → more shifts.
 */
const SENSITIVITY_EFFECT: Record<Sensitivity, number> = {
  low: 1.5,
  medium: 1.0,
  high: 0.6,
};

export interface LevelShiftOptions {
  /** Detection threshold knob. Default `'medium'`. */
  sensitivity?: Sensitivity;
  /** Minimum points on each side of a shift. Default 3. */
  minSegmentPoints?: number;
}

/** Sharpness at or above which a shift reads as an abrupt step rather than a drift. */
const ABRUPT_SHARPNESS = 2;

/** Plain-language phrasing of a level shift: abrupt vs gradual, up vs down. */
function levelShiftLabel(direction: LevelShift['direction'], sharpness: number): string {
  const abrupt = sharpness >= ABRUPT_SHARPNESS;
  if (direction === 'increase') {
    return abrupt ? 'abrupt step up' : 'gradual climb';
  }
  return abrupt ? 'abrupt step down' : 'gradual decline';
}

/** Median of the absolute sample-to-sample changes (0 for < 2 points). */
function typicalStep(values: number[]): number {
  if (values.length < 2) {
    return 0;
  }
  const diffs = values.slice(1).map((v, i) => Math.abs(v - (values[i] ?? v)));
  return median(diffs);
}

/**
 * Detect level shifts via recursive CUSUM change-point analysis. Returns the
 * shifts in time order.
 */
export function detectLevelShifts(points: TimeseriesPoint[], options: LevelShiftOptions = {}): LevelShift[] {
  const values = points.map((p) => p.value);
  const minPts = options.minSegmentPoints ?? 3;
  const sigma = standardDeviation(values);
  if (values.length < 2 * minPts || sigma <= EPSILON) {
    return [];
  }

  const threshold = SENSITIVITY_EFFECT[options.sensitivity ?? 'medium'];
  const step = typicalStep(values) || EPSILON;

  const recurse = (lo: number, hi: number): LevelShift[] => {
    const len = hi - lo + 1;
    if (len < 2 * minPts) {
      return [];
    }

    const sub = values.slice(lo, hi + 1);
    const mu = mean(sub);
    // Cumulative deviation from the sub-range mean; S has len + 1 entries.
    const cumulative = sub.reduce<number[]>(
      (acc, v) => {
        acc.push((acc.at(-1) ?? 0) + (v - mu));
        return acc;
      },
      [0],
    );

    // Candidate split `s` begins the right side; pick the largest |S_s|.
    const candidates = Array.from({ length: len - 2 * minPts + 1 }, (_, i) => minPts + i);
    const split = candidates.reduce(
      (best, s) => (Math.abs(cumulative[s] ?? 0) > Math.abs(cumulative[best] ?? 0) ? s : best),
      minPts,
    );

    const leftMean = mean(sub.slice(0, split));
    const rightMean = mean(sub.slice(split));
    const effect = Math.abs(rightMean - leftMean) / sigma;
    if (effect < threshold) {
      return [];
    }

    const m = lo + split;
    const pivot = points[m];
    const jump = Math.abs((values[m] ?? 0) - (values[m - 1] ?? 0));
    if (!pivot) {
      return [];
    }

    const direction = rightMean >= leftMean ? 'increase' : 'decrease';
    const sharpness = jump / step;
    const shift: LevelShift = {
      label: levelShiftLabel(direction, sharpness),
      timestamp: pivot.timestamp,
      magnitude: rightMean - leftMean,
      direction,
      beforeMean: leftMean,
      afterMean: rightMean,
      sharpness,
    };
    return [...recurse(lo, m - 1), shift, ...recurse(m, hi)];
  };

  return recurse(0, values.length - 1);
}
